import React, { useEffect, useState } from "react";
import SubHeader from "../../components/header/SubHeader";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts, addRecommendedProducts, resetAddRecommendedProductsStatus } from "../../store/product-slice";
// import Button from "../../components/common/button/Button";
import Stack from "../../components/ui/Stack";
import SearchInput from "../../components/common/form/SearchInput";
import ConfirmModal from "../../components/common/modal/ConfirmModal";
import AlertMessage from "../../components/ui/AlertMessage";
import Icon from "../../components/ui/Icon";

const ProductRecommendation = () => {
    const dispatch = useDispatch();
    const userRole = useSelector((state) => state.auth.selectedRole);
    const { items: products, fetchStatus, error, addRecommendedProductsStatus } = useSelector((state) => state.product);

    const [searchText, setSearchText] = useState("");
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [alertInfo, setAlertInfo] = useState(null);

    useEffect(() => {
        if (userRole) {
            dispatch(fetchProducts({ ...userRole }));
        }
    }, [dispatch, userRole]);

    useEffect(() => {
        if (products.length > 0) {
            const recommended = products.filter(p => p.featured > 0).map(p => p.id);
            setSelectedProducts(recommended);
        }
    }, [products]);

    useEffect(() => {
        if (addRecommendedProductsStatus === "success") {
            setAlertInfo({
                message: "Recommended products updated successfully.",
                variant: "success",
            });
            dispatch(resetAddRecommendedProductsStatus());

            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } else if (addRecommendedProductsStatus === "failed") {
            setAlertInfo({
                message: error || "Failed to update recommended products.",
                variant: "danger",
            });
            dispatch(resetAddRecommendedProductsStatus());
        }
    }, [addRecommendedProductsStatus, error, dispatch]);

    const handleCheckboxChange = (productId) => {
        setSelectedProducts(prevState =>
            prevState.includes(productId)
                ? prevState.filter(id => id !== productId)
                : [...prevState, productId]
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setShowConfirm(true);
    };

    const confirmHandler = () => {
        setShowConfirm(false);
        const data = {
            id: userRole.scopeId,
            products: selectedProducts
        };
        dispatch(addRecommendedProducts({ roleSign: userRole.roleSign, data }));
    };

    const handleClear = () => {
        setSelectedProducts([]);
    };

    const filteredProducts = products
        .filter(product =>
            product.name.toLowerCase().includes(searchText.toLowerCase())
        )
        .sort((a, b) => (b.featured || 0) - (a.featured || 0));

    return (
        <>
            <SubHeader title="Recommended Products" >
                <button className="btn btn-primary text-white" onClick={handleClear}>Clear</button>
            </SubHeader>
            <div className="container p-0 ms-0">
                {fetchStatus === "loading" && <p>Loading ...</p>}
                {fetchStatus === "failed" && <p className="text-danger">{error}</p>}

                {fetchStatus === "success" && (
                    <>
                        <Stack className="justify-content-between align-items-center mb-4">
                            <SearchInput
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search products..."
                                columns={{ md: 4, lg: 3 }}
                            />
                            <p className="mb-0 fs-md">
                                <span className="text-secondary">Selected:</span>
                                <span className="text-primary ms-1">{selectedProducts.length}</span>
                            </p>
                        </Stack>

                        <form id="recommendation-form" onSubmit={handleSubmit}>
                            <div className="row g-3">
                                {filteredProducts.map((product) => (
                                    <div key={product.id} className="col-md-4 col-lg-3">
                                        <div className="card h-100 shadow-sm border-0 bg-light">
                                            <div className="card-body d-flex align-items-center">
                                                <div className="form-check mb-0">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input mt-0"
                                                        id={`prod-${product.id}`}
                                                        checked={selectedProducts.includes(product.id)}
                                                        onChange={() => handleCheckboxChange(product.id)}
                                                    />
                                                    <label
                                                        className="form-check-label ms-2 text-dark fw-medium pointer"
                                                        htmlFor={`prod-${product.id}`}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {product.name}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {filteredProducts.length === 0 && (
                                <div className="text-center py-5">
                                    <p className="text-muted">No products found matching your search.</p>
                                </div>
                            )}

                        </form>
                    </>
                )}
            </div>

            <div className="tableEditAction">
                <button
                    form="recommendation-form"
                    type="submit"
                    className="btn bg-primary bg-opacity-75 rounded-pill py-3"
                    disabled={addRecommendedProductsStatus === "loading" || fetchStatus !== "success"}
                >
                    {addRecommendedProductsStatus === "loading" ? (
                        <div className="spinner-border spinner-border-sm text-light" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    ) : (
                        <Icon name="save" height="24" width="24" />
                    )}
                </button>
            </div>

            <ConfirmModal
                isOpen={showConfirm}
                onCancel={() => setShowConfirm(false)}
                onConfirm={confirmHandler}
                item={{ confirmMessage: "Are you sure you want to save these recommended products?" }}
            />

            {alertInfo && (
                <AlertMessage
                    message={alertInfo.message}
                    variant={alertInfo.variant}
                    duration={3000}
                    onClose={() => setAlertInfo(null)}
                />
            )}
        </>
    );
};

export default ProductRecommendation;
